<template>
    <div class="form">
        <transition name="fade">
            <div v-if="status == 'loading'" class="form__overlay">
                <spinner-component></spinner-component>
            </div>
        </transition>

        <transition name="fade">
            <div v-if="status == 'success'" class="form__alertSuccess" role="alert">
                <fa-icon :icon="['fal', 'check']" class="mr-05"></fa-icon> {{ successText }}
            </div>
            <div v-if="status == 'error'" class="form__alertError" role="alert">
                {{ $helpers.trans('forms.messages.error') }}
            </div>
        </transition>

        <transition name="fade">
            <form
                v-show="status == 'pending'"
                ref="form"
                :id="`form${name}`"
                class="form__body"
                enctype="multipart/form-data"
                @submit.prevent="send()">

                <slot>
                    <div v-for="(input, index) in formInputs" :key="index" class="form-group">
                        <component :is="input.component" :input="input"></component>
                        <label 
                            v-if="!['checkbox-group', 'radio-group', 'locationPicker', 'file'].includes(input.type)" 
                            :for="input.name" 
                            class="form-label">
                            <span class="form-label__content">
                                {{ input.label }}
                            </span>
                        </label>
                        <span class="bar"></span>
                        <div class="invalid-feedback"></div>
                        <small v-if="input.description" class="form-text text-muted">{{ input.description }}</small>
                    </div>
                </slot>

                <input type="text" name="fax_number" value="" style="position: absolute; left: -9999px;" autocomplete="nope">
                <input type="hidden" name="user_identification" :value="timehash">      
                <input v-if="type" type="hidden" name="type" :value="type">
                <input v-if="spamFilter" type="text" name="donotfillthisfield" value="" style="position: absolute; left: -9999px;" autocomplete="nope">

                <slot name="submit" v-bind:submit="send">
                    <button class="btn btn-primary" type="submit">{{ $helpers.trans('messages.send') }}</button>
                </slot>

            </form>
        </transition>
    </div>
</template>
<script>
import http from 'axios'
import SpinnerComponent from '#app#/components/common/Spinner.vue'

export default {
    components: {
        SpinnerComponent,
    },

    props: {
        form: {
            type: Object,
            default: null,
        },
        formData: {
            default: () => [],
        },
        name: {
            type: String,
            default: 'Default'
        },
        action: {
            type: String,
            default: '/crm/lead'
        },
        method: {
            type: String,
            default: 'POST'
        },
        eventCategory: {
            type: String,
            default: 'ContactForm'
        },
        eventAction: {
            type: String,
            default: 'submit'
        },
        eventLabel: {
            type: String,
            default: '',
        },
        eventValue: {
            type: Number,
            default: null
        },
        append: {
            type: Object,
            default: null
        },
        spamFilter: {
            type: Boolean,
            default: true,
        },
        type: String,
    },
    data() {
        return {
            timehash: window.TIMEHASH,
            formEl: null,
            loading: false,
            status: 'pending',
            successText: (this.form ? this.form.localized.confirmation_text : null) || this.$helpers.trans('forms.messages.sent'),
        }
    },

    mounted() {
        this.formEl = this.$refs.form;
    },

    computed: {
        formInputs() {
            let formData = this.formData;

            if(!Array.isArray(formData)) {
                formData = JSON.parse(formData);
            }

            return formData.map(input => {
                input['component'] = () => System.import(`#app#/components/common/FormElements/${input.type}.vue` /* webpackChunkName: "formElement-[request]" */);
                return input;
            });
        }
    },

    methods: {
        send() {
            if (typeof gaTracking.sendEvent === "function") { 
                gaTracking.sendEvent(
                    this.eventCategory,
                    this.eventAction,
                    this.form ? this.form.name : this.eventLabel,
                    this.eventValue
                );
            }

            // Display loading spinner
            this.status = 'loading';

            // Prepareing form data
            let formData = new FormData(this.formEl);

            // Attaching uploaded files
            const fileInputElement = this.formEl.querySelector('input[name="attachments[]"]');
            if(fileInputElement) {
                Array.from(fileInputElement.files).forEach(file => formData.append("attachments[]", file));
            }

            // Append additional data
            if(this.append) {
                Object.keys(this.append).forEach(key => {
                    formData.append(key, this.append[key]);
                });
            }

            // Sending data to server
            http({
                method: this.method,
                url: this.action,
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                data: formData
            }).then(res => {
                // Handle form success
                this.$emit('success', res);
                this.status = 'success';
                
                if(res.data.msg && typeof res.data.msg === 'string') {
                    this.successText = res.data.msg;
                }

                if (typeof gaTracking.sendEvent === "function") { 
                    gaTracking.sendEvent(
                        this.eventCategory,
                        'success',
                        this.form ? this.form.name : this.eventLabel,
                        this.eventValue
                    );
                }
                if(typeof fbq !== 'undefined') {
                    fbTracking.sendEvent('Lead');
                }

            }).catch(error => {
                this.$emit('error', error);

                if (typeof gaTracking.sendEvent === "function") { 
                    gaTracking.sendEvent(
                        this.eventCategory,
                        'error',
                        this.form ? this.form.name : this.eventLabel,
                        this.eventValue
                    );
                }

                if (error.response && error.response.status == 422) {
                    // Handle validation errors
                    this.status = 'pending';

                    this.formEl.querySelectorAll('.is-invalid').forEach(input => input.classList.remove('is-invalid'));
                    this.formEl.querySelectorAll('.invalid-feedback').forEach(inputFeedback => inputFeedback.textContent = '');
                    
                    let errors = error.response.data.errors;
                    Object.keys(errors).forEach(key => {
                        this.formEl.querySelectorAll(`input[name="${key}"], input[name="${key}[]"], select[name="${key}"]`)
                                .forEach(input => input.classList.add('is-invalid'));
                                
                        let errorResult = errors[key].join();
                        let invalidFeedbackEl = this.formEl.querySelector(`input[name="${key}"] ~ .invalid-feedback, select[name="${key}"] ~ .invalid-feedback, .invalid-feedback[name="${key}"]`)
                        if(invalidFeedbackEl) {
                            invalidFeedbackEl.textContent = errorResult;
                        }
                    });
                } else {
                    // Handle general errors
                    this.status = 'error';
                }
            })
        }
    }
}
</script>



// WEBPACK FOOTER //
// ../default-js/js/components/common/Form.vue